import { CompanionDevice, getElectronAPI } from 'flow/utils/electron-api';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';

let deviceLookup: Promise<CompanionDevice | null> | undefined;

let resolvedDevice: CompanionDevice | null = null;

export const loadCompanionDevice = (): Promise<CompanionDevice | null> => {
    if (deviceLookup) {
        return deviceLookup;
    }

    const electronAPI = getElectronAPI();
    if (!electronAPI) {
        deviceLookup = Promise.resolve(null);
        return deviceLookup;
    }

    deviceLookup = electronAPI
        .getCompanionDevice()
        .then((device) => {
            resolvedDevice = device;
            addUserPhoneLogEntry(
                device
                    ? `Companion device: ${device.deviceId} (${device.name})`
                    : 'No companion device available from the main process',
            );
            return device;
        })
        .catch((error) => {
            addUserPhoneLogEntry(`Companion device lookup failed: ${error}`);
            // Uncache the failure so the next caller retries: a transient IPC error at boot must
            // not leave this install id-less for the whole session, ignoring every addressed
            // command (handleCompanionCallControl fails closed on a missing own id).
            deviceLookup = undefined;
            return null;
        });

    return deviceLookup;
};

export const loadCompanionDeviceId = (): Promise<string | null> =>
    loadCompanionDevice().then((device) => device?.deviceId ?? null);

export const getCompanionDevice = (): CompanionDevice | null => resolvedDevice;

export const getCompanionDeviceId = (): string | null =>
    resolvedDevice?.deviceId ?? null;

export const getCompanionDeviceName = (): string | null =>
    resolvedDevice?.name ?? null;

export const renameCompanionDevice = (
    name: string,
): Promise<CompanionDevice | null> => {
    const electronAPI = getElectronAPI();
    if (!electronAPI) {
        return Promise.resolve(null);
    }

    return electronAPI
        .setCompanionDeviceName(name)
        .then((device) => {
            if (device) {
                resolvedDevice = device;
                deviceLookup = Promise.resolve(device);
            }
            addUserPhoneLogEntry(
                device
                    ? `Companion device renamed to ${device.name}`
                    : 'Companion device rename returned no device',
            );
            return device;
        })
        .catch((error) => {
            addUserPhoneLogEntry(`Companion device rename failed: ${error}`);
            return null;
        });
};

export const resetCompanionDeviceIdForTests = () => {
    deviceLookup = undefined;
    resolvedDevice = null;
};
